import { useEffect, useRef, useState } from 'react';
import type { CasaBau, CasaItens, CasaOficio, CasaPreso, FerramentaRegras, MinhaCasa, PackItem } from '../../api/types';
import { arteDoGiro, BAU_COMUM, ehBau, ehMobilia, FERRAMENTAS, girosDe, mobiliaDe, oficioDe } from '../../data/mobiliaInfo';
import { estadoDaColocacao, type EstadoColocacao } from '../../world/casas';
import { t } from '../../i18n/idioma';
import { nomeDoItem } from '../../i18n/nomes';
import { ItemIcon } from '../ItemIcon';
import { dica, rotulo, rotulo as dicaRotulo, type DicaTexto } from '../../ui/dica';
import { useQuantas } from '../Quantas';

/**
 * A interface da casa no mundo (techspec-casa-interface-e-baus, protótipo `casa-interface-prototipo.html`):
 * - a barra no alto, no meio, sempre que você está dentro: a casa, o seu papel e os três contadores (baús, presos, ofícios);
 * - Colocar em cartas (abas e busca) e a faixa de colocação embaixo (estado, impacto, altura, teclas);
 * - o menu da peça (Mover, Girar, Subir, Descer, Soltar; Abrir, Sentar, Craftar/Treinar);
 * - o baú (rótulo, quem abre, ocupação) e Gerenciar com abas (Itens, Baús, Ofícios, Acesso);
 * - a placa da casa, como no UO.
 */

const RARIDADES = ['comum', 'incomum', 'raro', 'epico', 'lendario'] as const;
const nomeRaridade = (r: number) => `raridade.${RARIDADES[Math.max(0, Math.min(4, r))]}`;

/* ================================================================ contadores */
export type Vaga = 'bau' | 'preso' | 'ofi';

const vagasDoBau = (b: CasaBau, casa: CasaItens) => b.vagas ?? casa.vagasPorBau;
const usadasDoBau = (b: CasaBau) => b.usadas ?? b.itens.length;

export function usoDe(casa: CasaItens, v: Vaga): { usado: number; max: number } {
  if (v === 'bau') return { usado: casa.baus.length, max: casa.maxBaus };
  if (v === 'ofi') return { usado: casa.oficios?.length ?? 0, max: casa.maxOficios ?? 0 };
  return { usado: casa.presos.length, max: casa.maxPresos };
}
export const vagaDoItem = (defId: string): Vaga => (ehBau(defId) ? 'bau' : oficioDe(defId) ? 'ofi' : 'preso');

/** Os três contadores (§02): ícone, usado/máximo e a barrinha; âmbar a partir de `casa.quase`%, vermelho cheio. */
export function Contadores({ casa, grande = false, mais = null }: { casa: CasaItens; grande?: boolean; mais?: Vaga | null }) {
  const quase = casa.quase ?? 80;
  const gratis = casa.baus.filter(b => mobiliaDe(b.defId ?? BAU_COMUM)?.gratis).length;
  const vagasTot = casa.baus.reduce((s, b) => s + vagasDoBau(b, casa), 0);
  const itensTot = casa.baus.reduce((s, b) => s + usadasDoBau(b), 0);
  // Baú comum grátis: até o teto dele na casa, e cada um ainda ocupa uma das vagas de baú (Renato, 04/11).
  const tetoGratis = casa.tetosDeBau?.[BAU_COMUM] || casa.maxBaus;
  const cabeGratis = Math.max(0, Math.min(tetoGratis - gratis, casa.maxBaus - casa.baus.length));
  const dicaBaus: DicaTexto = {
    texto: t('casa.cont.tipBaus', { teto: tetoGratis, gratis, comprados: casa.baus.length - gratis, max: casa.maxBaus }),
    rodape: t('casa.cont.tipBausGuarda', { itens: itensTot, vagas: vagasTot }),
    ...(cabeGratis > 0
      ? { ok: t('casa.cont.tipBausCabe', { n: cabeGratis }) }
      : { alerta: t(casa.baus.length >= casa.maxBaus ? 'casa.cont.tipBausSemVaga' : 'casa.cont.tipBausNoTeto') })
  };
  const dados: { v: Vaga; rotulo: string; icone: string; dica: Omit<DicaTexto, 'titulo'> }[] = [
    { v: 'bau', rotulo: t('casa.cont.baus'), icone: BAU_COMUM, dica: dicaBaus },
    { v: 'preso', rotulo: t('casa.cont.presos'), icone: 'mobilia-cadeira', dica: { texto: t('casa.cont.tipPresos') } },
    { v: 'ofi', rotulo: t('casa.cont.oficios'), icone: 'oficio-bigorna',
      dica: { texto: (casa.oficios ?? []).map(o => nomeDoItem(o.defId, o.nome)).join(', ') || t('casa.cont.tipOficios') } }
  ];
  return (
    <div className={`casa-cnts${grande ? ' casa-cnts--grande' : ''}`}>
      {dados.map(d => {
        const { usado, max } = usoDe(casa, d.v);
        const f = max ? usado / max : 1;
        const cls = usado >= max ? ' is-cheio' : f * 100 >= quase ? ' is-quase' : '';
        return (
          <div key={d.v} className={`casa-cnt${cls}`} tabIndex={0} {...dica({ ...d.dica, titulo: `${d.rotulo} ${usado}/${max}` })}
            aria-label={t('casa.cont.aria', { rotulo: d.rotulo, n: usado, max })}>
            <span className="casa-cnt__ic"><ItemIcon id={d.icone} size={grande ? 22 : 18} /></span>
            <span className="casa-cnt__v">
              {grande && <span className="casa-cnt__r">{d.rotulo}</span>}
              {usado}<small>/{max}</small>{mais === d.v && <span className="casa-cnt__mais">+1</span>}
            </span>
            <span className="casa-cnt__b"><i style={{ width: `${Math.min(100, f * 100)}%` }} /></span>
          </div>
        );
      })}
    </div>
  );
}

/* ================================================================ barra da casa (§02) */
export function CasaBarra({ nome, papel, casa, colocando, janela, onColocar, onGerir }: {
  nome: string;
  papel: string;
  casa: CasaItens | null;
  /** O que está sendo posto (o contador dessa vaga mostra +1); null fora da colocação. */
  colocando: Vaga | null;
  janela: 'colocar' | 'gerir' | null;
  onColocar(): void;
  onGerir(): void;
}) {
  const mexe = papel === 'dono' || papel === 'codono';
  return (
    <div className="casa-barra" role="region" aria-label={nome}>
      <span className="casa-barra__casa"><b>{nome}</b></span>
      <span className={`casa-papel casa-papel--${papel}`}>{t(`casa.papel.${papel}`)}</span>
      {mexe && casa && <Contadores casa={casa} mais={colocando} />}
      {mexe && !colocando && (
        <>
          <button type="button" className="btn btn--tiny btn--hud" aria-pressed={janela === 'colocar'} onClick={onColocar}>{t('casa.barra.colocar')}</button>
          <button type="button" className="btn btn--tiny btn--hud" aria-pressed={janela === 'gerir'} onClick={onGerir}>{t('casa.barra.gerenciar')}</button>
        </>
      )}
    </div>
  );
}

/* ================================================================ Colocar (§03) */
type Aba = 'tudo' | 'baus' | 'moveis' | 'luz' | 'oficios' | 'outros';
interface Carta { readonly defId: string; readonly item: PackItem | null; readonly qty: number; readonly cat: Exclude<Aba, 'tudo'> }

export function ColocarGump({ casa, mochila, continuar, onContinuar, onBauGratis, onItem }: {
  casa: CasaItens | null;
  mochila: readonly PackItem[];
  continuar: boolean;
  onContinuar(v: boolean): void;
  onBauGratis(): void;
  onItem(item: PackItem): void;
}) {
  const [aba, setAba] = useState<Aba>('tudo');
  const [busca, setBusca] = useState('');
  if (!casa) return <p className="empty">{t('comum.montando')}</p>;
  // Uma carta por item da mochila (a mobília repetida vira ×N); o Baú comum grátis sempre primeiro.
  const porDef = new Map<string, Carta>();
  porDef.set(BAU_COMUM, { defId: BAU_COMUM, item: null, qty: 0, cat: 'baus' });
  for (const i of mochila) {
    if (i.equippedSlot) continue;
    const ja = porDef.get(i.defId);
    if (ja && ja.item) { porDef.set(i.defId, { ...ja, qty: ja.qty + i.qty }); continue; }
    if (i.defId === BAU_COMUM) continue;
    const m = mobiliaDe(i.defId);
    const cat: Carta['cat'] = ehBau(i.defId) ? 'baus' : oficioDe(i.defId) ? 'oficios' : !m ? 'outros' : m.luz || m.conjunto === 'luz' ? 'luz' : 'moveis';
    porDef.set(i.defId, { defId: i.defId, item: i, qty: i.qty, cat });
  }
  const todas = [...porDef.values()];
  const q = busca.trim().toLocaleLowerCase();
  const vis = todas.filter(c => (aba === 'tudo' || c.cat === aba) && (!q || nomeDoItem(c.defId, c.item?.name).toLocaleLowerCase().includes(q)));
  const grupos: [Exclude<Aba, 'tudo'>, string | null][] = aba === 'tudo'
    ? [['baus', t('casa.colocar.abas.baus')], ['moveis', t('casa.colocar.abas.moveis')], ['luz', t('casa.colocar.abas.luz')], ['oficios', t('casa.colocar.oficiosT')], ['outros', t('casa.colocar.abas.outros')]]
    : [[aba, null]];
  const abas: Aba[] = ['tudo', 'baus', 'moveis', 'luz', 'oficios', ...(todas.some(c => c.cat === 'outros') ? ['outros' as const] : [])];
  return (
    <div className="casa-colocar">
      <Contadores casa={casa} grande />
      <div className="casa-chips" role="tablist">
        {abas.map(a => (
          <button key={a} type="button" role="tab" className="casa-chip" aria-selected={aba === a} onClick={() => setAba(a)}>
            {t(`casa.colocar.abas.${a}`)} <small>{a === 'tudo' ? todas.length : todas.filter(c => c.cat === a).length}</small>
          </button>
        ))}
      </div>
      <input className="casa-busca" type="search" value={busca} onChange={e => setBusca(e.target.value)}
        placeholder={t('casa.colocar.busca')} aria-label={t('casa.colocar.busca')} />
      {vis.length === 0 && <p className="empty">{t('casa.colocar.mochilaVazia')}</p>}
      {grupos.map(([cat, rotulo]) => {
        const lista = vis.filter(c => c.cat === cat);
        if (lista.length === 0) return null;
        return (
          <section key={cat}>
            {rotulo && <h3 className="casa-sec">{rotulo}</h3>}
            <div className="casa-cartas">
              {lista.map(c => <CartaDeColocar key={c.defId} carta={c} casa={casa}
                onClick={() => (c.item ? onItem(c.item) : onBauGratis())} />)}
            </div>
          </section>
        );
      })}
      <label className="casa-continuar">
        <input type="checkbox" checked={continuar} onChange={e => onContinuar(e.target.checked)} />
        {t('casa.colocar.continuar')}
      </label>
    </div>
  );
}

function CartaDeColocar({ carta, casa, onClick }: { carta: Carta; casa: CasaItens; onClick(): void }) {
  const m = mobiliaDe(carta.defId);
  const v = vagaDoItem(carta.defId);
  const { usado, max } = usoDe(casa, v);
  const gratis = !carta.item;
  const teto = casa.tetosDeBau?.[carta.defId] ?? 0;
  const noTeto = v === 'bau' && teto > 0 && casa.baus.filter(b => (b.defId ?? BAU_COMUM) === carta.defId).length >= teto;
  const naCasa = v === 'ofi' && (casa.oficios ?? []).some(o => o.defId === carta.defId);
  let sub: { txt: string; cls: string };
  if (usado >= max) sub = { txt: t(`casa.colocar.semVaga.${v}`), cls: 'is-ruim' };
  else if (noTeto) sub = { txt: t('casa.colocar.tetoBau', { n: teto }), cls: 'is-ruim' };
  else if (naCasa) sub = { txt: t('casa.colocar.jaNaCasa'), cls: 'is-ruim' };
  else if (v === 'bau') sub = { txt: t('casa.colocar.vagas', { n: vagasPadrao(carta.defId, casa) }), cls: 'is-bau' };
  else if (v === 'ofi') sub = { txt: t('casa.colocar.oficioBonus'), cls: 'is-ofi' };
  else if (m) sub = { txt: t('casa.colocar.alturaN', { n: m.altura }), cls: '' };
  else sub = { txt: '', cls: '' };
  const desligada = usado >= max || noTeto || naCasa;
  const nome = nomeDoItem(carta.defId, carta.item?.name);
  const rar = v === 'ofi' ? 'lendario' : (m?.raridade ?? 0) >= 1 ? 'incomum' : 'comum';
  return (
    <button type="button" className={`casa-carta${gratis ? ' is-gratis' : ''}${v === 'ofi' ? ' is-ofi' : ''}`} disabled={desligada}
      {...(gratis ? dica({ titulo: nome, texto: t('casa.colocar.gratisDica', { n: vagasPadrao(BAU_COMUM, casa) }), alerta: desligada ? sub.txt : undefined })
        : desligada && sub.txt ? dica({ titulo: nome, texto: sub.txt }) : rotulo(nome))} onClick={onClick}>
      <span className={`casa-carta__r is-${rar}`} aria-hidden="true" />
      {gratis ? <span className="casa-carta__q is-gratis">{t('casa.colocar.gratis')}</span>
        : carta.qty > 1 ? <span className="casa-carta__q">×{carta.qty}</span> : null}
      <span className="casa-carta__arte">
        {ehMobilia(carta.defId) ? <img src={arteDoGiro(carta.defId, 0)} alt="" /> : <ItemIcon id={carta.defId} size={40} />}
      </span>
      <span className="casa-carta__n">{nome}</span>
      {sub.txt && <span className={`casa-carta__s ${sub.cls}`}>{sub.txt}</span>}
    </button>
  );
}

/** As vagas que um baú daquele tipo terá: um igual já posto diz; senão, o do comum (o servidor confere ao pôr). */
function vagasPadrao(defId: string, casa: CasaItens): number {
  const igual = casa.baus.find(b => (b.defId ?? BAU_COMUM) === defId);
  if (igual?.vagas) return igual.vagas;
  const m = mobiliaDe(defId);
  if (!m || m.gratis) return casa.vagasPorBau;
  return [30, 40, 50, 60, 75][Math.max(0, Math.min(4, m.raridade))];
}

/** O chip que fica no lugar da janela Colocar enquanto a peça está sendo posta (§03). */
export function ColocarMinimizado() {
  return (
    <div className="casa-colocar-mini" role="status">
      <b>{t('casa.colocar.titulo')}</b><span>{t('casa.colocar.escolhaPiso')}</span>
    </div>
  );
}

/* ================================================================ faixa de colocação (§04) */
export function FaixaColocacao({ defId, nome, movendo, casa, podeGirar, podeContinuar, onSubir, onDescer, onGirar, onCancelar }: {
  defId: string;
  nome: string;
  movendo: boolean;
  casa: CasaItens | null;
  podeGirar: boolean;
  /** Shift + clique fixa e continua (tem mais da mesma peça). */
  podeContinuar: boolean;
  onSubir(): void;
  onDescer(): void;
  onGirar(): void;
  onCancelar(): void;
}) {
  // O motor avalia o lugar sob o cursor a cada quadro; a faixa lê o último estado (sem refazer a conta).
  const [est, setEst] = useState<EstadoColocacao | null>(null);
  useEffect(() => {
    const id = window.setInterval(() => {
      const e = estadoDaColocacao();
      setEst(a => (a && e && a.ok === e.ok && a.motivo === e.motivo && a.dz === e.dz && a.max === e.max && a.emCima === e.emCima && a.giro === e.giro) || (!a && !e) ? a : e);
    }, 120);
    return () => window.clearInterval(id);
  }, []);
  const v = vagaDoItem(defId);
  const uso = casa ? usoDe(casa, v) : null;
  const giro = est?.giro ?? 0;
  const dz = est?.dz ?? 0, max = est?.max ?? 0;
  const ondeTxt = dz === 0 ? t('casa.faixa.noPiso') : est?.emCima ? t('casa.faixa.emCima', { nome: nomeDoItem(est.emCima) }) : t('casa.faixa.acima', { n: dz });
  return (
    <div className="casa-faixa" role="status">
      <span className="casa-faixa__arte">
        {ehMobilia(defId) ? <img src={arteDoGiro(defId, giro)} alt="" /> : <ItemIcon id={defId} size={44} />}
      </span>
      <span className="casa-faixa__t">
        <b><em>{movendo ? t('casa.faixa.movendo') : t('casa.faixa.colocando')}</em>{nome}</b>
        {!est ? <span className="casa-faixa__st is-neutro">{t('casa.faixa.aponte')}</span>
          : est.ok ? <span className="casa-faixa__st">✓ {t('casa.faixa.ok')}{podeContinuar && <> · <kbd>Shift</kbd> {t('casa.faixa.shift')}</>}</span>
            : <span className="casa-faixa__st is-ruim">✕ {t(est.motivo ?? 'casa.colocar.fora')}</span>}
        <span className="casa-faixa__imp">
          {movendo || !uso ? t('casa.faixa.semImpacto')
            : t(`casa.faixa.impacto.${v}`, { de: uso.usado, para: uso.usado + 1, max: uso.max, vagas: casa ? vagasPadrao(defId, casa) : 0 })}
        </span>
      </span>
      <span className="casa-faixa__ac">
        <span className="casa-alt" {...dica({ texto: t('casa.colocar.alturaDica') })}>
          <span className="casa-alt__txt">{dz}<small>{t('casa.faixa.de', { max })}</small><small>{ondeTxt}</small></span>
          <span className="casa-alt__trilho">
            {Array.from({ length: Math.floor(max / 5) + 1 }, (_, i) => <s key={i} style={{ bottom: `${max ? (i * 5) / max * 100 : 0}%` }} />)}
            <i style={{ bottom: `${max ? dz / max * 100 : 0}%` }} />
          </span>
        </span>
        <button type="button" className="casa-ib" disabled={dz >= max} onClick={onSubir}><span>▲</span>{t('casa.colocar.subir')}<kbd>PgUp</kbd></button>
        <button type="button" className="casa-ib" disabled={dz <= 0} onClick={onDescer}><span>▼</span>{t('casa.colocar.descer')}<kbd>PgDn</kbd></button>
        <button type="button" className="casa-ib" disabled={!podeGirar} onClick={onGirar}><span>⟳</span>{t('casa.menu.girar')}<kbd>R</kbd></button>
        <button type="button" className="casa-ib is-sair" onClick={onCancelar}><span>✕</span>{movendo ? t('casa.faixa.voltar') : t('comum.cancelar')}<kbd>Esc</kbd></button>
      </span>
    </div>
  );
}

/* ================================================================ menu da peça (§05) */
export type PecaDoMenu =
  | { readonly tipo: 'bau'; readonly bau: CasaBau; readonly dz: number }
  | { readonly tipo: 'oficio'; readonly oficio: CasaOficio; readonly dz: number }
  | { readonly tipo: 'preso'; readonly preso: CasaPreso; readonly dz: number; readonly emCima: string | null };

export function MenuDaPeca({ x, y, peca, casa, podeMexer, busy, bonus, onFechar, onAbrir, onUsar, onSentar, onMover, onGirar, onAltura, onSoltar }: {
  x: number;
  y: number;
  peca: PecaDoMenu;
  casa: CasaItens;
  podeMexer: boolean;
  busy: boolean;
  /** O resumo do bônus da ferramenta (quando vale). */
  bonus?: string;
  onFechar(): void;
  onAbrir(): void;
  onUsar(): void;
  onSentar(): void;
  onMover(): void;
  onGirar(): void;
  onAltura(d: number): void;
  onSoltar(): void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const fora = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) onFechar(); };
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('pointerdown', fora, true);
    window.addEventListener('keydown', tecla, true);
    return () => {
      window.removeEventListener('pointerdown', fora, true);
      window.removeEventListener('keydown', tecla, true);
    };
  }, [onFechar]);
  const defId = peca.tipo === 'bau' ? (peca.bau.defId ?? BAU_COMUM) : peca.tipo === 'oficio' ? peca.oficio.defId : peca.preso.defId;
  const giro = peca.tipo === 'bau' ? peca.bau.giro : peca.tipo === 'oficio' ? peca.oficio.giro : (peca.preso.giro ?? 0);
  const m = mobiliaDe(defId);
  const altura = m?.altura ?? 1;
  const dz = peca.dz;
  let titulo: string, nome: string, linha: React.ReactNode, principal: React.ReactNode = null;
  let soltarDesligado = false;
  if (peca.tipo === 'bau') {
    const b = peca.bau, vg = vagasDoBau(b, casa), n = usadasDoBau(b);
    titulo = t('casa.peca.bau');
    nome = b.rotulo ? `${nomeDoItem(defId)} · ${b.rotulo}` : nomeDoItem(defId);
    linha = (
      <>
        {m?.gratis ? t('casa.colocar.gratis') : t(nomeRaridade(m?.raridade ?? 0))} · {n}/{vg} · {t(`casa.perm.${b.perm}`)}
        <span className={`casa-barrinha${n >= vg ? ' is-cheio' : n / vg >= (casa.quase ?? 80) / 100 ? ' is-quase' : ''}`}><i style={{ width: `${Math.min(100, n / vg * 100)}%` }} /></span>
      </>
    );
    principal = <button type="button" className="btn btn--tiny btn--primary" disabled={b.trancado} {...(b.trancado ? rotulo(t('casa.bau.trancado')) : {})} onClick={onAbrir}>{t('casa.menu.abrir')}</button>;
    soltarDesligado = n > 0;
  } else if (peca.tipo === 'oficio') {
    const o = peca.oficio;
    titulo = t('casa.peca.oficio');
    nome = nomeDoItem(o.defId, o.nome);
    linha = <>{t(`compra.oficio.${o.oficio}`)} · {o.bonusAtivo ? (bonus ?? t('casa.oficio.bonusValendo')) : t(o.motivo ?? 'casa.oficio.semBonus')}</>;
    principal = <button type="button" className="btn btn--tiny btn--primary" onClick={onUsar}>{o.oficio === 'treino' ? t('casa.menu.treinar') : t('casa.peca.craftar')}</button>;
  } else {
    const p = peca.preso;
    titulo = t('casa.peca.peca');
    nome = nomeDoItem(p.defId, p.nome);
    linha = <>{m ? t(nomeRaridade(m.raridade)) : ''}{m ? ' · ' : ''}{dz === 0 ? t('casa.faixa.noPiso') : peca.emCima ? t('casa.faixa.emCima', { nome: nomeDoItem(peca.emCima) }) : t('casa.faixa.acima', { n: dz })}</>;
    if (m?.assento?.some(Boolean)) principal = <button type="button" className="btn btn--tiny" onClick={onSentar}>{t('casa.peca.sentar')}</button>;
  }
  const left = Math.min(x + 12, window.innerWidth - 256);
  const top = Math.max(60, Math.min(y - 24, window.innerHeight - 230));
  return (
    <div ref={ref} className="gump casa-mp" style={{ left, top }} role="menu" aria-label={nome}>
      <div className="gump__head"><span className="gump__title">{titulo}</span>
        <button type="button" className="win__close" aria-label={t('comum.fechar')} onClick={onFechar}>✕</button></div>
      <div className="casa-mp__corpo">
        <div className="casa-mp__info">
          <span className="casa-mp__arte">{ehMobilia(defId) ? <img src={arteDoGiro(defId, giro)} alt="" /> : <ItemIcon id={defId} size={36} />}</span>
          <span><b>{nome}</b><small>{linha}</small></span>
        </div>
        {principal && <div className="casa-mp__pri">{principal}</div>}
        {podeMexer ? (
          <>
            <div className="casa-mp__acoes">
              <button type="button" className="casa-ib" disabled={busy} onClick={onMover}><span>✥</span>{t('casa.menu.mover')}</button>
              <button type="button" className="casa-ib" disabled={busy || girosDe(defId) < 2} onClick={onGirar}><span>⟳</span>{t('casa.menu.girar')}</button>
              <button type="button" className="casa-ib" disabled={busy || dz + 1 + altura > 20} onClick={() => onAltura(1)}><span>▲</span>{t('casa.colocar.subir')}</button>
              <button type="button" className="casa-ib" disabled={busy || dz <= 0} onClick={() => onAltura(-1)}><span>▼</span>{t('casa.colocar.descer')}</button>
              <button type="button" className="casa-ib is-sair" disabled={busy || soltarDesligado} {...(soltarDesligado ? rotulo(t('casa.peca.esvazie')) : {})}
                onClick={onSoltar}><span>⤓</span>{peca.tipo === 'oficio' ? t('casa.gerir.recolher') : t('casa.gerir.soltar')}</button>
            </div>
            {soltarDesligado && <p className="casa-mp__nota">{t('casa.peca.esvazie')}</p>}
          </>
        ) : <p className="casa-mp__nota">{t('casa.peca.soDono')}</p>}
      </div>
    </div>
  );
}

/* ================================================================ Gerenciar (§07) */
/** Os papéis que o dono dá (a ordem do seletor). */
export const PAPEIS = ['convidado', 'amigo', 'codono', 'banido'] as const;
export type PapelDaLista = typeof PAPEIS[number];

export function GerirGump({ casa, busy, minha, nome, onSoltar, onRecolherBau, onAbrirBau, onMostrar, onMover, onPapel, onRemover, onAberta, onRenomear }: {
  casa: CasaItens | null;
  busy: boolean;
  /** Só para o dono: a lista de acesso (aba Acesso) e o nome da casa. */
  minha: MinhaCasa | null;
  nome: string;
  onSoltar(id: string): void;
  onRecolherBau(id: string): void;
  onAbrirBau(id: string): void;
  onMostrar(x: number, y: number, z: number): void;
  onMover(alvo: { tipo: 'bau' | 'preso'; id: string }): void;
  onPapel(nome: string, papel: PapelDaLista): void;
  onRemover(nome: string): void;
  onAberta(aberta: boolean): void;
  onRenomear(nome: string): void;
}) {
  const [aba, setAba] = useState<'itens' | 'baus' | 'oficios' | 'acesso'>('itens');
  const [conj, setConj] = useState('todos');
  const [editando, setEditando] = useState<string | null>(null);
  if (!casa) return <p className="empty">{t('comum.montando')}</p>;
  const dono = casa.papel === 'dono' && !!minha;
  const mexe = casa.papel === 'dono' || casa.papel === 'codono';
  const abas = [
    ['itens', t('casa.gerir.abaItens'), casa.presos.length],
    ['baus', t('casa.gerir.abaBaus'), casa.baus.length],
    ['oficios', t('casa.gerir.abaOficios'), casa.oficios?.length ?? 0],
    ...(dono ? [['acesso', t('casa.gerir.abaAcesso'), (minha!.acesso ?? []).length] as const] : [])
  ] as const;
  const conjuntos = [...new Set(casa.presos.map(p => mobiliaDe(p.defId)?.conjunto ?? 'outros'))];
  const presos = casa.presos.filter(p => conj === 'todos' || (mobiliaDe(p.defId)?.conjunto ?? 'outros') === conj);
  const linha = (defId: string, giro: number, nomeL: string, sub: React.ReactNode, acoes: React.ReactNode, key: string) => (
    <li key={key} className="casa-lin">
      <span className="casa-lin__ic">{ehMobilia(defId) ? <img src={arteDoGiro(defId, giro)} alt="" /> : <ItemIcon id={defId} size={26} />}</span>
      <span className="casa-lin__nm"><span>{nomeL}</span><small>{sub}</small></span>
      <span className="casa-lin__ac">{acoes}</span>
    </li>
  );
  const mostrar = (x: number, y: number, z: number) => (
    <button type="button" className="casa-mini" {...rotulo(t('casa.gerir.mostrar'))} aria-label={t('casa.gerir.mostrar')} onClick={() => onMostrar(x, y, z)}>◎</button>
  );
  const mover = (tipo: 'bau' | 'preso', id: string) => mexe && (
    <button type="button" className="casa-mini" disabled={busy} {...rotulo(t('casa.menu.mover'))} aria-label={t('casa.menu.mover')} onClick={() => onMover({ tipo, id })}>✥</button>
  );
  return (
    <div className="casa-gerir">
      <div className="casa-gerir__nome">
        {editando !== null ? (
          <>
            <input className="casa-busca" maxLength={32} value={editando} autoFocus placeholder={nome} aria-label={t('casa.gerir.renomear')}
              onChange={e => setEditando(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { onRenomear(editando); setEditando(null); } if (e.key === 'Escape') setEditando(null); }} />
            <button type="button" className="btn btn--tiny" disabled={busy} onClick={() => { onRenomear(editando); setEditando(null); }}>{t('comum.salvar')}</button>
          </>
        ) : (
          <>
            <b>{nome}</b>
            {dono && <button type="button" className="casa-mini" {...rotulo(t('casa.gerir.renomear'))} aria-label={t('casa.gerir.renomear')} onClick={() => setEditando(casa.nome ?? '')}>✎</button>}
          </>
        )}
      </div>
      <Contadores casa={casa} grande />
      <div className="casa-abas2" role="tablist">
        {abas.map(([k, rot, n]) => (
          <button key={k} type="button" role="tab" className="casa-aba2" aria-selected={aba === k} onClick={() => setAba(k)}>{rot} <small>{n}</small></button>
        ))}
      </div>
      {aba === 'itens' && (
        <>
          {conjuntos.length > 1 && (
            <div className="casa-chips casa-chips--linha">
              <button type="button" className="casa-chip" aria-selected={conj === 'todos'} onClick={() => setConj('todos')}>{t('casa.gerir.todos')} <small>{casa.presos.length}</small></button>
              {conjuntos.map(c => (
                <button key={c} type="button" className="casa-chip" aria-selected={conj === c} onClick={() => setConj(c)}>
                  {c === 'outros' ? t('casa.gerir.outros') : t(`mobilia.conjunto.${c}`)} <small>{casa.presos.filter(p => (mobiliaDe(p.defId)?.conjunto ?? 'outros') === c).length}</small>
                </button>
              ))}
            </div>
          )}
          {presos.length === 0 && <p className="empty">{t('casa.gerir.semPresos')}</p>}
          <ul className="casa-lista2">
            {presos.map(p => linha(p.defId, p.giro ?? 0, `${nomeDoItem(p.defId, p.nome)}${p.qty > 1 ? ` ×${p.qty}` : ''}`,
              mobiliaDe(p.defId)?.luz ? t('casa.gerir.luz') : t(nomeRaridade(mobiliaDe(p.defId)?.raridade ?? 0)),
              <>{mostrar(p.x, p.y, p.z)}{mover('preso', p.id)}{mexe && <button type="button" className="casa-mini" disabled={busy} {...rotulo(t('casa.gerir.soltar'))} aria-label={t('casa.gerir.soltar')} onClick={() => onSoltar(p.id)}>⤓</button>}</>,
              p.id))}
          </ul>
        </>
      )}
      {aba === 'baus' && (
        <>
          <p className="casa-nota">{t('casa.gerir.bausNota', { max: casa.maxBaus, vagas: casa.vagasPorBau })}</p>
          <ul className="casa-lista2">
            {casa.baus.map((b, k) => {
              const vg = vagasDoBau(b, casa), n = usadasDoBau(b);
              return linha(b.defId ?? BAU_COMUM, b.giro, nomeDoBau(b, k),
                <>
                  {n}/{vg} · {t(`casa.perm.${b.perm}`)}
                  <span className={`casa-barrinha${n >= vg ? ' is-cheio' : n / vg >= (casa.quase ?? 80) / 100 ? ' is-quase' : ''}`}><i style={{ width: `${Math.min(100, n / vg * 100)}%` }} /></span>
                </>,
                <>
                  {mostrar(b.x, b.y, b.z)}
                  <button type="button" className="casa-mini" disabled={b.trancado} {...rotulo(t('casa.gerir.abrir'))} aria-label={t('casa.gerir.abrir')} onClick={() => onAbrirBau(b.id)}>▤</button>
                  {mover('bau', b.id)}
                  {mexe && <button type="button" className="casa-mini" disabled={busy || n > 0} {...rotulo(n > 0 ? t('casa.peca.esvazie') : t('casa.gerir.soltar'))} aria-label={t('casa.gerir.soltar')} onClick={() => onRecolherBau(b.id)}>⤓</button>}
                </>, b.id);
            })}
            {Array.from({ length: Math.max(0, casa.maxBaus - casa.baus.length) }, (_, i) => (
              <li key={`livre${i}`} className="casa-lin is-livre"><span className="casa-lin__ic" /><span className="casa-lin__nm"><small>{t('casa.gerir.vagaLivre')}</small></span><span /></li>
            ))}
          </ul>
        </>
      )}
      {aba === 'oficios' && (
        <ul className="casa-lista2">
          {FERRAMENTAS.map(f => {
            const o = (casa.oficios ?? []).find(x => x.defId === f.defId);
            return o
              ? linha(o.defId, o.giro, nomeDoItem(o.defId, o.nome),
                <span className={o.bonusAtivo ? 'is-ok' : 'is-ruim'}>{t(`compra.oficio.${o.oficio}`)} · {o.bonusAtivo ? t('casa.oficio.bonusValendo') : t(o.motivo ?? 'casa.oficio.semBonus')}</span>,
                <>{mostrar(o.x, o.y, o.z)}{mexe && <button type="button" className="casa-mini" disabled={busy} {...rotulo(t('casa.gerir.recolher'))} aria-label={t('casa.gerir.recolher')} onClick={() => onSoltar(o.id)}>⤓</button>}</>, o.id)
              : (
                <li key={f.defId} className="casa-lin is-livre">
                  <span className="casa-lin__ic"><ItemIcon id={f.defId} size={26} /></span>
                  <span className="casa-lin__nm"><span>{nomeDoItem(f.defId)}</span><small>{t('casa.oficio.ondeComprar', { npc: t(f.titulo) })}</small></span>
                  <span />
                </li>
              );
          })}
        </ul>
      )}
      {aba === 'acesso' && dono && (
        <AcessoLista minha={minha!} busy={busy} onPapel={onPapel} onRemover={onRemover} onAberta={onAberta} />
      )}
    </div>
  );
}

/** O resumo do bônus em uma linha: "+20% perícia · −10% tempo · +5 p.p. excepcional · 10% poupar material". */
export function resumoBonus(r: FerramentaRegras): string {
  const p: string[] = [];
  if (r.ganho) p.push(t('compra.lend.ganho', { n: r.ganho }));
  if (r.atributo) p.push(t('compra.lend.atributo', { n: r.atributo }));
  if (r.tempo) p.push(t('compra.lend.tempo', { n: r.tempo }));
  if (r.excepcional) p.push(t('compra.lend.excepcional', { n: r.excepcional }));
  if (r.economia) p.push(t('compra.lend.economia', { n: r.economia }));
  return p.join(' · ');
}

/** O nome do baú: o rótulo do dono, ou o nome do tipo e o número. */
export function nomeDoBau(b: CasaBau, indice: number): string {
  if (b.rotulo) return b.rotulo;
  return `${nomeDoItem(b.defId ?? BAU_COMUM)} ${indice + 1}`;
}

/**
 * A lista de acesso (techspec-casas §03.13), do dono: casa aberta, quem está na lista e com que papel. Serve na aba
 * Acesso de Gerenciar casa e na aba Minha casa do corretor.
 */
export function AcessoLista({ minha, busy, onPapel, onRemover, onAberta }: {
  minha: MinhaCasa;
  busy: boolean;
  onPapel(nome: string, papel: PapelDaLista): void;
  onRemover(nome: string): void;
  onAberta(aberta: boolean): void;
}) {
  const [nome, setNome] = useState('');
  const [papel, setPapel] = useState<PapelDaLista>('convidado');
  const lista = minha.acesso ?? minha.convidados.map(n => ({ nome: n, papel: 'convidado' }));
  const membros = lista.filter(a => a.papel !== 'banido');
  const banidos = lista.filter(a => a.papel === 'banido');
  const adicionar = () => {
    const n = nome.trim();
    if (!n || busy) return;
    onPapel(n, papel);
    setNome('');
  };
  const pessoa = (a: { nome: string; papel: string }) => (
    <li key={a.nome} className={`casa-pessoa${a.papel === 'banido' ? ' is-banido' : ''}`}>
      <span className="casa-pessoa__nome">{a.nome}</span>
      <select value={a.papel} disabled={busy} aria-label={t('casa.acesso.papelDe', { nome: a.nome })}
        onChange={e => onPapel(a.nome, e.target.value as PapelDaLista)}>
        {PAPEIS.map(p => <option key={p} value={p}>{t(`casa.papel.${p}`)}</option>)}
      </select>
      <button type="button" className="casa-mini" disabled={busy} onClick={() => onRemover(a.nome)}
        aria-label={t('casa.remover')} {...rotulo(t('casa.remover'))}>✕</button>
    </li>
  );
  return (
    <div className="casa-acesso">
      <label className="casa-acesso__aberta">
        <input type="checkbox" role="switch" checked={!!minha.aberta} disabled={busy} onChange={e => onAberta(e.target.checked)} />
        <span>
          <b>{t('casa.acesso.aberta')}</b>
          <small>{t(minha.aberta ? 'casa.acesso.abertaSim' : 'casa.acesso.abertaNao')}</small>
        </span>
      </label>
      <h3 className="casa-sec">{t('casa.acesso.lista')} <em>{membros.length}/{minha.maxConvidados}</em></h3>
      {membros.length === 0 && <p className="empty">{t('casa.semConvidados')}</p>}
      <ul className="casa-acesso__lista">{membros.map(pessoa)}</ul>
      <div className="casa-acesso__novo">
        <input type="text" maxLength={16} value={nome} disabled={busy}
          onChange={e => setNome(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') adicionar(); }}
          aria-label={t('casa.nomeDoConvidado')} placeholder={t('casa.nomeDoConvidado')} />
        <select value={papel} disabled={busy} onChange={e => setPapel(e.target.value as PapelDaLista)} aria-label={t('casa.acesso.papel')}>
          {PAPEIS.map(p => <option key={p} value={p}>{t(`casa.papel.${p}`)}</option>)}
        </select>
        <button type="button" className="btn btn--tiny" disabled={busy || !nome.trim()} onClick={adicionar}>{t('casa.acesso.adicionar')}</button>
      </div>
      <h3 className="casa-sec">{t('casa.acesso.banidos')} <em>{banidos.length}/{minha.maxBanidos ?? 50}</em></h3>
      {banidos.length === 0 && <p className="empty">{t('casa.acesso.semBanidos')}</p>}
      <ul className="casa-acesso__lista">{banidos.map(pessoa)}</ul>
      <dl className="casa-legenda">
        {(['codono', 'amigo', 'convidado', 'banido'] as const).map(p => (
          <div key={p}><dt><span className={`casa-papel casa-papel--${p}`}>{t(`casa.papel.${p}`)}</span></dt><dd>{t(`casa.acesso.pode.${p}`)}</dd></div>
        ))}
      </dl>
    </div>
  );
}

/* ================================================================ baú (§06) */
export function BauGump({ bau, vagas, mochila, busy, podeConfig, quase = 80, onGuardar, onTirar, onConfig }: {
  bau: CasaBau;
  vagas: number;
  mochila: readonly PackItem[];
  busy: boolean;
  /** Dono e co-dono: rótulo e "quem abre". */
  podeConfig: boolean;
  quase?: number;
  onGuardar(item: PackItem, qty: number): void;
  onTirar(id: string, qty: number): void;
  onConfig(cfg: { rotulo?: string; perm?: number }): void;
}) {
  // Pilha pergunta "Quantas?" ao guardar e ao tirar (techspec-pilha-de-pecas §13).
  const quantas = useQuantas();
  const guardar = async (item: PackItem) => {
    const n = await quantas.pedir({ defId: item.defId, nome: item.name, max: item.qty, titulo: t('casa.bau.guardar') });
    if (n) onGuardar(item, n);
  };
  const tirar = async (item: { id: string; defId: string; nome?: string; qty: number }) => {
    const n = await quantas.pedir({ defId: item.defId, nome: item.nome, max: item.qty, titulo: t('quantas.tirarBau') });
    if (n) onTirar(item.id, n);
  };
  const [rotulo, setRotulo] = useState(bau.rotulo ?? '');
  useEffect(() => { setRotulo(bau.rotulo ?? ''); }, [bau.rotulo]);
  if (bau.trancado) {
    return <div className="casa-bau is-trancado"><p className="casa-aviso">{t('casa.bau.trancado')}</p></div>;
  }
  const defId = bau.defId ?? BAU_COMUM;
  const n = bau.itens.length;
  const cheio = n >= vagas;
  const soltos = mochila.filter(i => !i.equippedSlot);
  const celulas = Array.from({ length: Math.max(vagas, n) }, (_, i) => bau.itens[i] ?? null);
  const salvarRotulo = () => { if (rotulo !== (bau.rotulo ?? '')) onConfig({ rotulo }); };
  return (
    <>
      <div className="casa-bau__cab">
        {podeConfig ? (
          <>
            <input type="text" maxLength={24} value={rotulo} disabled={busy} placeholder={t('casa.bau.rotuloDica')}
              aria-label={t('casa.bau.rotulo')} onChange={e => setRotulo(e.target.value)} onBlur={salvarRotulo}
              onKeyDown={e => { if (e.key === 'Enter') salvarRotulo(); }} />
            <select value={bau.perm} disabled={busy} aria-label={t('casa.bau.quemAbre')} onChange={e => onConfig({ perm: Number(e.target.value) })}>
              {[3, 2, 1].map(p => <option key={p} value={p}>{t(`casa.perm.${p}`)}</option>)}
            </select>
          </>
        ) : <span className="casa-nota">{t(`casa.perm.${bau.perm}`)}</span>}
        <div className="casa-bau__cap">
          <span>{nomeDoItem(defId)}{mobiliaDe(defId)?.gratis ? ` · ${t('casa.colocar.gratis')}` : ''}</span>
          <span className={`casa-barrinha${cheio ? ' is-cheio' : n / vagas >= quase / 100 ? ' is-quase' : ''}`}><i style={{ width: `${Math.min(100, n / vagas * 100)}%` }} /></span>
          <span>{n}/{vagas}</span>
        </div>
      </div>
      {n > vagas && <p className="casa-aviso">{t('casa.bau.acima', { n, vagas })}</p>}
      <div className="casa-bau">
        <div className="casa-bau__slots">
          {celulas.map((i, k) => i
            ? (
              <button key={i.id} type="button" className={`casa-bau__slot${k >= vagas ? ' is-acima' : ''}`} disabled={busy}
                {...dica({ titulo: `${nomeDoItem(i.defId, i.nome)}${i.qty > 1 ? ` ×${i.qty}` : ''}`, texto: t('casa.bau.cliqueTira') })} onClick={() => void tirar(i)}>
                <ItemIcon id={i.defId} size={30} quantity={i.qty > 1 ? i.qty : undefined} />
              </button>
            )
            : <span key={`v${k}`} className="casa-bau__slot is-vazio" />)}
        </div>
        <div>
          <h3 className="casa-sec">{t('casa.bau.mochilaT')}</h3>
          {soltos.length === 0 && <p className="empty">{t('casa.colocar.mochilaVazia')}</p>}
          <ul className="casa-bau__mochila">
            {soltos.map(i => (
              <li key={i.id}>
                <button type="button" disabled={busy || cheio} {...dicaRotulo(cheio ? t('casa.bau.cheio') : t('casa.bau.guardar'))} onClick={() => void guardar(i)}>
                  <ItemIcon id={i.defId} size={24} />
                  <span>{nomeDoItem(i.defId, i.name)}{i.qty > 1 ? ` ×${i.qty}` : ''}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
      {quantas.dialogo}
    </>
  );
}

/* ================================================================ placa (§09) */
export function PlacaGump({ nome, nomeProprio, dono, modelo, aberta, papel, busy, onGerenciar, onRenomear }: {
  nome: string;
  /** O nome que o dono deu (vazio = o padrão), para o campo de renomear. */
  nomeProprio: string | null;
  dono: string | null;
  modelo: string;
  aberta: boolean;
  papel: string;
  busy: boolean;
  onGerenciar?(): void;
  onRenomear?(nome: string): void;
}) {
  const [editando, setEditando] = useState<string | null>(null);
  const temPapel = ['dono', 'codono', 'amigo', 'convidado', 'banido'].includes(papel);
  const salvar = () => { if (editando !== null) { onRenomear?.(editando); setEditando(null); } };
  return (
    <div className="casa-placa">
      {editando !== null ? (
        <div className="casa-gerir__nome">
          <input className="casa-busca" maxLength={32} value={editando} autoFocus placeholder={nome} aria-label={t('casa.gerir.renomear')}
            onChange={e => setEditando(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') salvar(); if (e.key === 'Escape') setEditando(null); }} />
          <button type="button" className="btn btn--tiny" disabled={busy} onClick={salvar}>{t('comum.salvar')}</button>
        </div>
      ) : <p className="casa-placa__nome">{nome}</p>}
      <dl className="casa-placa__dados">
        <dt>{t('casa.placa.dono')}</dt><dd>{dono ?? t('casa.placa.semDono')}</dd>
        {modelo && <><dt>{t('casa.placa.modelo')}</dt><dd>{t(`casa.modelo.${modelo}`)}</dd></>}
        <dt>{t('casa.placa.porta')}</dt><dd>{aberta ? t('casa.placa.aberta') : t('casa.placa.particular')}</dd>
        {temPapel && <><dt>{t('casa.placa.voce')}</dt><dd><span className={`casa-papel casa-papel--${papel}`}>{t(`casa.papel.${papel}`)}</span></dd></>}
      </dl>
      {(onGerenciar || onRenomear) && editando === null && (
        <div className="casa-placa__ac">
          {onGerenciar && <button type="button" className="btn btn--tiny" onClick={onGerenciar}>{t('casa.barra.gerenciar')}</button>}
          {onRenomear && <button type="button" className="btn btn--tiny" onClick={() => setEditando(nomeProprio ?? '')}>{t('casa.gerir.renomear')}</button>}
        </div>
      )}
    </div>
  );
}

/** O nome da casa na placa e na barra: o que o dono deu, ou "Casa de {dono}". */
export function nomeDaCasa(nome: string | null | undefined, dono: string | null | undefined): string {
  if (nome) return nome;
  return dono ? t('casa.placa.nomePadrao', { dono }) : t('casa.placa.semNome');
}
